import React, { useState } from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import * as style from './ArticleNumberedHeadingWithImage.module.scss';
import ArticleNumberedHeading from '../ArticleNumberedHeading';
import ArticleImageBlock from '../ArticleImageBlock';
import useOnClickOutside from '../../../hooks/useOnClickOutsideExceptClasses';

/**
 * Composite block: Numbered Heading + Image with "awards' details" functionality that displays an overlay over the image.
 */
const ArticleNumberedHeadingWithImage = (props) => {
  const {
    anchor,
    altText,
    hoverTitle,
    number,
    title,
    titleTag,
    awardsList,
    imgData,
    authorName,
    websiteName,
    websiteUrl,
    websiteUrlTarget,
    isNoFollow,
    className,
  } = props;

  const [hoveredItemIndex, setHoveredItemIndex] = useState(-1);
  const [isOverlayTextAnimating, setIsOverlayTextAnimating] = useState(false);
  const [doNotCloseElementsClassName] = useState(uniqid('class-'));

  const setHoveredItemIndexRespectAnimation = (index) => {
    if (!isOverlayTextAnimating) {
      setHoveredItemIndex(index);
    }
  };

  useOnClickOutside(doNotCloseElementsClassName, () => {
    setHoveredItemIndexRespectAnimation(-1);
  });

  return (
    <div className={`${style.wrapper} ${className}`}>
      <ArticleNumberedHeading
        title={title}
        titleTag={titleTag}
        variant="full"
        number={number}
        awardsList={awardsList}
        awardsElementsClassName={doNotCloseElementsClassName}
        setHoveredItemIndex={setHoveredItemIndexRespectAnimation}
        id={anchor}
      />
      <ArticleImageBlock
        imgData={imgData}
        altText={altText}
        hoverTitle={hoverTitle}
        authorName={authorName}
        websiteName={websiteName}
        websiteUrl={websiteUrl}
        awardsList={awardsList}
        awardsIndexToDisplay={hoveredItemIndex}
        textOverlayClassName={doNotCloseElementsClassName}
        setIsOverlayTextAnimating={setIsOverlayTextAnimating}
        isNoFollow={isNoFollow}
        websiteUrlTarget={websiteUrlTarget}
      />
    </div>
  );
};
// TODO: document props
ArticleNumberedHeadingWithImage.defaultProps = {
  altText: 'image',
  anchor: '',
  authorName: '',
  awardsList: [],
  className: '',
  hoverTitle: '',
  isNoFollow: false,
  titleTag: 'h3',
  websiteName: '',
  websiteUrl: '',
  websiteUrlTarget: '_self',
};

ArticleNumberedHeadingWithImage.propTypes = {
  /** Numbered to be displayed in the circle */
  number: PropTypes.number.isRequired,
  /** Title text */
  title: PropTypes.string.isRequired,
  /** The Tag used to display the title (<h2>, <h3>, <h4>, <p>) */
  titleTag: PropTypes.string,
  /** List of awards - objects of the following format: { awardName, awardDescriptionPreselection }. */
  awardsList: PropTypes.array,
  /** Image data in gatsby-image format */
  imgData: PropTypes.any.isRequired,
  /** Image alt text for when image is not displayed (and SEO) */
  altText: PropTypes.string,
  /** Image title to display a tooltip on hover. */
  hoverTitle: PropTypes.string,
  /** Name of a photographer who took the picture. */
  authorName: PropTypes.string,
  /** Name of a website where the picture was published. */
  websiteName: PropTypes.string,
  /** Url of a website where the picture was published. */
  websiteUrl: PropTypes.string,
  /** The target to open a link in (e.g. _blank). */
  websiteUrlTarget: PropTypes.string,
  /** Make a link have ref='nofollow' attribute */
  isNoFollow: PropTypes.bool,
  /** Anchor passed to the heading block for anchor navigation */
  anchor: PropTypes.string,
  /** Optional class name */
  className: PropTypes.string,
};

export default ArticleNumberedHeadingWithImage;
